import { useLocation } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import CustomBreadcrumbs from "../components/navigation/breadcrumbs/CustomBreadcrumbs";
import { GPSCoorindate } from "../models/GPSCoorindate";
import { useState, useEffect, useMemo } from "react";
import EnhancedMapComponent from "../components/map/EnhancedMapComponent";
import MapDataSelector, { createMetricsArray } from "../components/map/MapDataSelector";

interface MapRoutePageProps {
  sessionId?: string;
  sessionName?: string;
}

interface LocationState {
  coordinates: GPSCoorindate[];
  plotData: any[];
  sessionId: string;
  sessionName: string;
  sensorId?: string;
}

// Extended GPS coordinate type with timestamp and metric value
interface ExtendedGPSCoordinate extends GPSCoorindate {
  timestamp?: string;
  metricValue?: number | null;
}

export default function MapRoutePage() {
  const location = useLocation();
  const state = location.state as LocationState;
  
  const [selectedMetric, setSelectedMetric] = useState<string>("v_speed");
  const [coordinates, setCoordinates] = useState<ExtendedGPSCoordinate[]>([]);
  const [plotData, setPlotData] = useState<any[]>([]);
  const [sessionName, setSessionName] = useState<string>("");
  const [sessionId, setSessionId] = useState<string>("");
  const [sensorId, setSensorId] = useState<string>("");
  
  // Get the metrics array to pass to EnhancedMapComponent
  const metrics = useMemo(() => createMetricsArray(), []);
  
  useEffect(() => {
    if (state) {
      const coords = state.coordinates || [];
      const data = state.plotData || [];
      
      // Check if coordinates have timestamps
      const hasTimestamps = coords.length > 0 && 'timestamp' in coords[0];
      
      // If coordinates don't have timestamps but plot data does, interpolate timestamps
      if (!hasTimestamps && data.length > 0 && 'timestamp' in data[0]) {
        console.log("Interpolating timestamps for coordinates");
        
        // Sort plot data by timestamp
        const sortedData = [...data].sort((a, b) => {
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        });
        
        // Get start and end times
        const startTime = new Date(sortedData[0].timestamp).getTime();
        const endTime = new Date(sortedData[sortedData.length - 1].timestamp).getTime();
        const timeRange = endTime - startTime;
        
        // Interpolate timestamps for coordinates based on their position in the array
        const coordsWithTimestamps = coords.map((coord, index) => {
          // Calculate a timestamp based on the position in the array
          const progress = coords.length > 1 ? index / (coords.length - 1) : 0;
          const timestamp = new Date(startTime + progress * timeRange).toISOString();
          
          return {
            ...coord,
            timestamp
          };
        });
        
        setCoordinates(coordsWithTimestamps);
      } else {
        setCoordinates(coords);
      }
      
      setPlotData(data);
      setSessionId(state.sessionId || "");
      setSessionName(state.sessionName || "");
      setSensorId(state.sensorId || "");
    }
  }, [state]);

  // Process data to link GPS coordinates with the selected metric
  const processedData = useMemo(() => {
    if (!coordinates.length || !plotData.length) {
      return coordinates.map(coord => ({
        ...coord,
        metricValue: null
      }));
    }

    // Sort plot data by timestamp for finding nearest points
    const sortedPlotData = [...plotData].sort((a, b) => {
      if (!a.timestamp || !b.timestamp) return 0;
      return a.timestamp.localeCompare(b.timestamp);
    });
    
    // Function to find the nearest data point by timestamp
    const findNearestDataPoint = (timestamp: string) => {
      if (!timestamp || !sortedPlotData.length) return null;
      
      // Convert timestamps to Date objects for comparison
      const targetTime = new Date(timestamp).getTime();
      
      // Find the closest timestamp in the plot data
      let closestIndex = 0;
      let minTimeDiff = Number.MAX_VALUE;
      
      for (let i = 0; i < sortedPlotData.length; i++) {
        if (!sortedPlotData[i].timestamp) continue;
        
        const currentTime = new Date(sortedPlotData[i].timestamp).getTime();
        const timeDiff = Math.abs(currentTime - targetTime);
        
        if (timeDiff < minTimeDiff) {
          minTimeDiff = timeDiff;
          closestIndex = i;
        }
      }
      
      return sortedPlotData[closestIndex];
    };
    
    // Match coordinates with plot data using nearest timestamp
    return coordinates.map(coord => {
      let matchingData = null;
      
      // If the coordinate has a timestamp, find the nearest data point
      if ('timestamp' in coord && coord.timestamp) {
        matchingData = findNearestDataPoint(coord.timestamp as string);
      } 
      // If no timestamp in coordinate, try to match by lat/lon
      else if (coord.lat && coord.lon) {
        // Find plot data with closest lat/lon
        matchingData = plotData.find(data => 
          data.lat === coord.lat && data.lon === coord.lon
        );
      }
      
      // Get the metric value from the matching data
      const metricValue = matchingData ? matchingData[selectedMetric] : null;
      
      return {
        ...coord,
        metricValue
      };
    });
  }, [coordinates, plotData, selectedMetric]);

  const handleMetricChange = (metric: string) => {
    setSelectedMetric(metric);
  };

  return (
    <Container fluid className="background-color text-white p-4">
      <Row>
        <Col>
          <CustomBreadcrumbs
            path={[
              { path: "/dashboard", title: "Dashboard" },
              { path: "/sessions", title: "Sessions" },
              { path: `/sessions/${sessionId}${sensorId ? `?sensorId=${sensorId}` : ''}`, title: sessionName }
            ]}
            page="Map Route"
          />
        </Col>
      </Row>
      <Container fluid className="pt-4 pb-2 px-1">
        <Row className="mb-4">
          <Col>
            <h1>Map Route</h1>
            <p className="text-muted">Visualize route data for {sessionName}</p>
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <MapDataSelector 
              selectedMetric={selectedMetric} 
              onMetricChange={handleMetricChange} 
            />
          </Col>
          <Col md={9}>
            <EnhancedMapComponent 
              points={processedData} 
              selectedMetric={selectedMetric}
              metrics={metrics}
              height="70vh" 
            />
          </Col>
        </Row>
      </Container>
    </Container>
  );
}
